import { MenuItem } from '@mui/material';
import RoundedSelect from 'common/components/RoundedSelect';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SelectValidator } from 'react-material-ui-form-validator';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

const CustomSelectWithInfiniteScroll = ({
    customMenuProps,
    id,
    name,
    type,
    onSubmit,
    selectedById,
    selectAll,
    fullWidth,
    selectNone,
    size = 'small',
    update,
    organizationId,
    fetchAction,
    label,
    i18nKeys,
    queryParams = {},
    disabled,
    queryOptions,
    idProperty = 'id',
    placeholderValue = 'all',
    searchParam = 'search',
    validators,
    errorMessages,
    sx,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    // const { enqueueSnackbar } = useSnackbar();
    const [items, setItems] = useState([]);
    const [displayValue, setDisplayValue] = useState('all');
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [initialSearch, setInitialSearch] = useState('');
    const [pending, setPending] = useState(false);
    const [pendingSelected, setPendingSelected] = useState(false);
    const loadingRef = useRef(false);
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const [wasSearching, setWasSearching] = useState(false);
    const [currentSelectedItem, setCurrentSelectedItem] = useState(selectedById);
    const [initialFilter, setInitialFilter] = useState('');

    useEffect(() => {
        if (selectedById) {
            setCurrentSelectedItem(selectedById);
        }
    }, [selectedById]);

    useEffect(() => {
        setDisplayValue(id || placeholderValue);
    }, [id, placeholderValue]);

    useEffect(() => {
        if (currentSelectedItem && id && id !== 'all') {
            setItems((prevItems) => {
                // Don't add duplicates
                if (!prevItems.some((item) => item?.[idProperty] === currentSelectedItem?.[idProperty])) {
                    return [...prevItems, currentSelectedItem];
                }
                return prevItems;
            });
        }
    }, [currentSelectedItem, id, idProperty]);

    const loadData = useCallback(
        (loadPage = 1, shouldReset = false) => {
            loadingRef.current = true;
            setPending(true);
            dispatch(
                fetchAction({
                    queryParams: {
                        ...queryParams,
                        page: loadPage,
                        limit: queryParams.limit ?? 50,
                        ...(search ? { [searchParam]: search } : {}),
                        ...(organizationId && { organizationId }),
                    },
                    ...(queryOptions ? { options: queryOptions } : {}),
                    onSuccess: (data) => {
                        setInitialLoadDone(true);
                        const pageSize = Number(queryParams.limit) || 50;
                        const resultTotal = Number(data?.total) || 0;
                        const resultTotalPages = Number(data?.totalPages);
                        setCurrentPage(loadPage);
                        setTotalPages(
                            resultTotalPages > 0 ? resultTotalPages : Math.ceil(resultTotal / pageSize),
                        );
                        setItems((prevItems) => {
                            // If shouldReset is true, only keep the selected item (if any)
                            const baseItems = shouldReset
                                ? currentSelectedItem && id && id !== 'all'
                                    ? [currentSelectedItem]
                                    : []
                                : prevItems;

                            // Filter out duplicates
                            const newItems = (data?.items || []).filter(
                                (item) => !baseItems.some((prevItem) => prevItem?.[idProperty] === item?.[idProperty]),
                            );

                            // Create the updated list
                            let updatedItems = [...baseItems, ...newItems];

                            // Make sure selected item is included if we have one
                            if (currentSelectedItem && id && id !== 'all') {
                                if (
                                    !updatedItems.some(
                                        (item) => item?.[idProperty] === currentSelectedItem?.[idProperty],
                                    )
                                ) {
                                    updatedItems = [currentSelectedItem, ...updatedItems];
                                }
                            }

                            update?.(updatedItems);
                            loadingRef.current = false;
                            setPending(false);
                            return updatedItems;
                        });
                    },
                    onFailure: () => {
                        setInitialLoadDone(true);
                        loadingRef.current = false;
                        setPending(false);
                        // enqueueSnackbar(t('app.common.error'), { variant: 'error' });
                    },
                }),
            );
        },
        [
            dispatch,
            fetchAction,
            queryParams,
            searchParam,
            search,
            organizationId,
            queryOptions,
            currentSelectedItem,
            id,
            update,
            idProperty,
        ],
    );

    useEffect(() => {
        if (
            id &&
            !items?.find((item) => item?.[idProperty] === id) &&
            currentSelectedItem?.[idProperty] !== id &&
            !pendingSelected &&
            id !== 'all'
        ) {
            setPendingSelected(true);
            dispatch(
                fetchAction?.({
                    // use findAll to use in future as multiple select too
                    queryParams: {
                        page: 1,
                        limit: 1,
                        ids: id,
                    },
                    onSuccess: (data) => {
                        const selectedItem = data?.items?.find((item) => item?.[idProperty] === id);
                        if (selectedItem) {
                            setCurrentSelectedItem(selectedItem);
                            if (!items?.find((item) => item?.[idProperty] === id)) {
                                setItems([selectedItem, ...items]);
                            }
                        }
                        setPendingSelected(false);
                    },

                    onFailure: () => {
                        setPendingSelected(false);
                    },
                }),
            );
        }
    }, [dispatch, fetchAction, items, pendingSelected, id, idProperty, currentSelectedItem]);

    const loadNextPage = useCallback(() => {
        const nextPage = Number(currentPage) + 1;
        const pageCount = Number(totalPages);
        if (loadingRef.current || !Number.isFinite(pageCount) || nextPage > pageCount) {
            return;
        }
        loadData(nextPage, false);
    }, [currentPage, loadData, totalPages]);

    useEffect(() => {
        if ((!initialLoadDone || JSON.stringify(queryParams) !== JSON.stringify(initialFilter)) && !pending) {
            loadData(1, true);
            setInitialFilter(queryParams);
        }
    }, [loadData, initialLoadDone, pending, queryParams, initialFilter]);

    useEffect(() => {
        if (open) {
            if (search && initialSearch !== search) {
                setWasSearching(true);
                setInitialSearch(search);
                // Reset items and load search results
                loadData(1, true);
            }
        } else {
            setSearch('');
        }
    }, [search, open, wasSearching, loadData, initialSearch]);

    useEffect(() => {
        if (open) {
            // If we were searching before and now opening without search,
            // we need to reload the full list
            if (wasSearching && !search) {
                setWasSearching(false);
                loadData(1, false);
            }
        }
    }, [open, wasSearching, search, loadData]);

    const handleChange = (e) => {
        const newValue = e.target.value;
        const selectedItem = items.find((item) => item?.[idProperty] === newValue);

        // Update the selected item if one is selected
        if (newValue !== 'all' && newValue !== null) {
            if (selectedItem) {
                setCurrentSelectedItem(selectedItem);
            }
        } else {
            setCurrentSelectedItem(null);
        }

        if (search) {
            setSearch('');
        }

        if (selectedItem) {
            onSubmit(e, type, selectedItem);
        } else {
            onSubmit(e, type);
        }
    };

    const { searchPlaceholder, allItems, selectItem, none } = i18nKeys || {};

    return (
        <RoundedSelect
            selectComponent={validators ? SelectValidator : undefined}
            bold={true}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            value={displayValue}
            name={name}
            validators={validators}
            errorMessages={errorMessages}
            onChange={handleChange}
            size={size}
            search={items?.length > 10 || Boolean(search) || pending}
            searchPlaceholder={t(searchPlaceholder)}
            onSearchFilter={setSearch}
            customMenuProps={customMenuProps}
            onReachBottom={loadNextPage}
            pending={pending || pendingSelected}
            fullWidth={fullWidth}
            disabled={disabled}
            sx={sx}
        >
            {selectAll ? (
                <MenuItem value="all">{t(allItems)}</MenuItem>
            ) : (
                <MenuItem disabled value={placeholderValue}>
                    <em>{t(selectItem)}</em>
                </MenuItem>
            )}
            {selectNone ? (
                <MenuItem value={null}>
                    <b>{t(none || 'common.none')}</b>
                </MenuItem>
            ) : null}
            {items?.map((item) => (
                <MenuItem key={item?.[idProperty]} value={item?.[idProperty]}>
                    {label(item)}
                </MenuItem>
            ))}
        </RoundedSelect>
    );
};

export default CustomSelectWithInfiniteScroll;
